import api from './api';

export interface SystemParameter {
  id: string;
  key: string;
  value: string;
  description?: string;
  category?: string;
  dataType: string;
  isActive: boolean;
  isEncrypted: boolean;
  createdBy?: string;
  updatedBy?: string;
  createdAt: string;
  updatedAt: string;
}

export class SystemParametersService {
  static async getParameter(key: string): Promise<SystemParameter | null> {
    try {
      const response = await api.get(`/system-parameters/${key}`);
      return response.data;
    } catch (error) {
      console.error(`Failed to get system parameter ${key}:`, error);
      return null;
    }
  }

  static async getAllParameters(): Promise<SystemParameter[]> {
    try {
      const response = await api.get('/system-parameters');
      return response.data;
    } catch (error) {
      console.error('Failed to get system parameters:', error);
      return [];
    }
  }

  static async getParametersByCategory(category: string): Promise<SystemParameter[]> {
    try {
      const response = await api.get(`/system-parameters?category=${category}`);
      return response.data;
    } catch (error) {
      console.error(`Failed to get system parameters for category ${category}:`, error);
      return [];
    }
  }

  // Helper methods for type-safe value retrieval
  static async getStringValue(key: string, defaultValue?: string): Promise<string | null> {
    const param = await this.getParameter(key);
    if (!param || !param.isActive) {
      return defaultValue || null;
    }
    return param.value;
  }

  static async getNumberValue(key: string, defaultValue?: number): Promise<number | null> {
    const param = await this.getParameter(key);
    if (!param || !param.isActive) {
      return defaultValue || null;
    }
    const numValue = parseFloat(param.value);
    return isNaN(numValue) ? (defaultValue || null) : numValue;
  }

  static async getBooleanValue(key: string, defaultValue?: boolean): Promise<boolean | null> {
    const param = await this.getParameter(key);
    if (!param || !param.isActive) {
      return defaultValue || null;
    }
    return param.value.toLowerCase() === 'true';
  }

  static async getJsonValue<T = any>(key: string, defaultValue?: T): Promise<T | null> {
    const param = await this.getParameter(key);
    if (!param || !param.isActive) {
      return defaultValue || null;
    }
    try {
      return JSON.parse(param.value);
    } catch {
      return defaultValue || null;
    }
  }

  static async updateParameter(key: string, data: { value?: string; description?: string; category?: string; dataType?: string; isActive?: boolean; isEncrypted?: boolean }): Promise<SystemParameter> {
    const response = await api.put(`/system-parameters/${key}`, data);
    return response.data;
  }

  static async createParameter(data: { key: string; value: string; description?: string; category?: string; dataType?: string; isActive?: boolean; isEncrypted?: boolean }): Promise<SystemParameter> {
    const response = await api.post('/system-parameters', data);
    return response.data;
  }

  static async deleteParameter(key: string): Promise<void> {
    await api.delete(`/system-parameters/${key}`);
  }
}
